import type { RouterOutputs } from "@api/trpc/routers/_app";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { QuestionCardError } from "@/features/questions/components/question-card-error-public-view";
import { QuestionCard } from "@/features/questions/components/question-card-public-view";
import { cn } from "@/lib/utils";
import EmptyQuestions from "./empty-questions";

interface QuestionsListProps {
  questions: RouterOutputs["questions"]["getWithFileNames"]["questions"];
  status: "pending" | "error" | "success";
  isFetching: boolean;
  isPolling?: boolean;
  retry: () => void;
  showActions: boolean;
}

const LoadingSkeleton = ({ index }: { index: number }) => (
  <div
    className={cn(
      "relative w-full border bg-muted/40 p-3 md:p-4 rounded-xl",
      "min-h-[120px] md:h-[180px]", // Added min-height for mobile
    )}
  >
    {/* Status indicator for desktop */}
    <div className="hidden md:flex w-full justify-end items-center gap-2 absolute right-4 top-4">
      <Skeleton className="h-6 w-20 rounded-full" />
    </div>

    <div className="flex flex-col md:flex-row w-full h-full gap-4 md:gap-6">
      {/* Question section */}
      <div className="w-full md:w-1/4 flex flex-col justify-center space-y-2">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-3/4" />
      </div>

      {/* Separator with dynamic orientation */}
      <Separator
        className={cn(
          "md:h-full h-[1px] md:w-[1px]",
          "bg-border/50", // Lighter separator
        )}
        orientation={window.innerWidth >= 768 ? "vertical" : "horizontal"}
      />

      {/* Answer section */}
      <div className="w-full md:w-3/4 flex flex-col justify-center space-y-2">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-2/3" />
      </div>
    </div>
  </div>
);

export const QuestionsList = ({
  questions,
  status,
  retry,
  showActions,
  isFetching,
  isPolling,
}: QuestionsListProps) => {
  if (status === "pending") {
    return ["a", "b"].map((id, index) => (
      <LoadingSkeleton key={`skeleton-${id}`} index={index} />
    ));
  }

  if (status === "error") {
    return <QuestionCardError retry={retry} />;
  }

  if (!isPolling && status === "success" && questions.length === 0) {
    return <EmptyQuestions />;
  }

  return (
    <>
      {questions?.map((questionData, index) => (
        <QuestionCard
          isPolling={isPolling}
          key={questionData.questions.id}
          index={index}
          question={questionData}
          showActions={showActions}
        />
      ))}
      {isPolling && <LoadingSkeleton index={0} />}
    </>
  );
};
